import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Marquee from "../components/Marquee";
import { HERO_IMAGES } from "../components/PageHero";
import { ChevronDown, ArrowRight } from "lucide-react";

const LOGO_URL = "/logo-gv.png";

const SectionHead = ({ num, title }) => (
  <div className="flex items-end gap-5 mb-10 sm:mb-14">
    <span className="ghost-num">{num}</span>
    <h2 className="display-title pb-1" style={{ fontSize: "clamp(1.9rem, 5vw, 3.6rem)", color: "var(--mm-text-primary)" }}>
      {title}
    </h2>
  </div>
);

export default function PublicHome() {
  const [chiSiamo, setChiSiamo] = useState([]);

  useEffect(() => {
    axios.get(`${API}/content/chi-siamo`).then(r => setChiSiamo(r.data)).catch(() => {});
  }, []);

  const mainText = chiSiamo[0];
  const otherTexts = chiSiamo.slice(1);

  return (
    <div className="page-container" data-testid="public-home" style={{ paddingTop: 0 }}>
      {/* FULL-SCREEN HERO */}
      <section className="full-hero">
        <div className="page-hero-img" style={{ backgroundImage: `url(${HERO_IMAGES.home})` }} />
        <div className="page-hero-overlay" />
        <div className="container relative h-full flex flex-col justify-end pb-16 sm:pb-20">
          <img src={LOGO_URL} alt="GoldVision" className="w-20 h-20 sm:w-24 sm:h-24 object-contain mb-6" />
          <p className="hero-kicker">Trading · Mindset · Crescita</p>
          <h1 className="display-title mb-6" style={{ fontSize: "clamp(2.6rem, 8.5vw, 7rem)", color: "#FFFFFF" }} data-testid="home-hero-title">
            Gold<span className="shimmer-gold">Vision</span>
          </h1>
          <p className="text-base md:text-lg max-w-xl mb-8" style={{ color: "rgba(255,255,255,0.8)" }}>
            Formazione, strumenti e supporto per il tuo percorso verso la libertà finanziaria.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/diventa-studente" className="px-8 py-4 font-bold uppercase text-sm transition-all duration-300 hover:scale-[1.03] flex items-center justify-center gap-2"
              style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.15em", background: "linear-gradient(135deg, #2FD8A8 0%, #1FB489 100%)", color: "#0A0A0A" }}
              data-testid="hero-studente-btn">
              Diventa Studente <ArrowRight size={17} />
            </Link>
            <Link to="/login" className="px-8 py-4 font-bold uppercase text-sm transition-all duration-300 hover:bg-white/10 flex items-center justify-center"
              style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.15em", color: "#2FD8A8", border: "1px solid #2FD8A8" }}
              data-testid="hero-login-btn">
              Accedi
            </Link>
          </div>
        </div>
        <div className="scroll-cue absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center" aria-hidden="true">
          <ChevronDown size={20} style={{ color: "#2FD8A8" }} />
        </div>
      </section>

      <div className="py-6 sm:py-10">
        <Marquee />
      </div>

      {/* 01 — CHI SIAMO */}
      {mainText && (
        <section className="container py-14 sm:py-20" data-testid="home-chi-siamo">
          <SectionHead num="01" title="Chi Siamo" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20 items-center">
            <div>
              <p className="text-base sm:text-lg leading-relaxed mb-12" style={{ color: "var(--mm-text-secondary)" }}>
                {mainText.content}
              </p>
              <div className="space-y-8">
                {otherTexts.map((item, i) => (
                  <div key={item.id} className="flex gap-5 pb-8" style={{ borderBottom: "1px solid var(--mm-border)" }} data-testid={`home-chi-card-${i}`}>
                    <span className="ghost-num-sm">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3 className="text-lg font-bold mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif", color: "var(--mm-gold)" }}>{item.title}</h3>
                      <p className="text-sm leading-relaxed" style={{ color: "var(--mm-text-secondary)" }}>{item.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative h-72 md:h-full min-h-[380px] overflow-hidden">
              <div className="absolute inset-0" style={{ backgroundImage: `url(${HERO_IMAGES.chisiamo})`, backgroundSize: "cover", backgroundPosition: "center" }} />
              <div className="absolute inset-0" style={{ border: "1px solid rgba(47,216,168,0.35)", margin: "14px" }} />
            </div>
          </div>
        </section>
      )}

      {/* CTA full-width band */}
      <section className="photo-band py-16 sm:py-24 text-center">
        <div className="photo-band-img" style={{ backgroundImage: `url(${HERO_IMAGES.cta})` }} />
        <div className="photo-band-overlay" />
        <div className="container relative">
          <p className="hero-kicker">Il tuo percorso inizia qui</p>
          <h2 className="display-title mb-6" style={{ fontSize: "clamp(2.2rem, 6vw, 4.8rem)", color: "#FFFFFF" }}>
            Pronto a iniziare?
          </h2>
          <p className="text-base mb-8 max-w-lg mx-auto" style={{ color: "rgba(255,255,255,0.8)" }}>
            Candidati come studente: valuteremo le tue risposte per capire se possiamo collaborare.
          </p>
          <Link to="/diventa-studente" className="inline-flex items-center gap-2 px-10 py-4 font-bold uppercase text-sm transition-all duration-300 hover:scale-[1.03]"
            style={{ fontFamily: "'Space Grotesk', sans-serif", letterSpacing: "0.15em", background: "linear-gradient(135deg, #2FD8A8 0%, #1FB489 100%)", color: "#0A0A0A" }}
            data-testid="cta-studente-btn">
            Diventa Studente <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
